import { ulid, safe, getRoughObjectSize, mapError } from '@w/utils'
import { prepareForLog } from './prepare-for-log/prepare-for-log'
import type { PayloadLimits } from './prepare-for-log/prepare-for-log'
import { prepareTelError } from './prepare-for-log/prepare-tel-error'
import type { TelErrorData } from './tel-error-data'
import {
  type SpanData,
  type Span,
  getBaggage,
  type ResourceUserInfo,
} from './tel-types'

export function _createSpan(
  data: SpanData,
  storage: {
    end: (patch: Partial<SpanData> & { id: string }) => void
    update: (patch: Partial<SpanData> & { id: string }) => void
    errors: {
      create: (
        originalError: unknown,
        data: TelErrorData,
        userInfo?: ResourceUserInfo,
        opts?: { dedupeBySpan?: boolean },
      ) => void
    }
  },
  opts?: {
    alreadyMasked?: boolean
    isSensitive?: (key: string, value: unknown) => boolean
    payloadLimits?: Partial<PayloadLimits>
  },
): Span {
  const createdAt = Date.now()
  const payloadLimits = opts?.payloadLimits
  const inputMaskOpts = opts?.alreadyMasked
    ? { isSensitive: () => false }
    : opts?.isSensitive
      ? { isSensitive: opts.isSensitive }
      : {}
  const outputMaskOpts = opts?.isSensitive
    ? { isSensitive: opts.isSensitive }
    : {}
  data.id = data.id || ulid()
  data.input = prepareForLog(data.input, {
    removeMark: true,
    ...payloadLimits,
    ...inputMaskOpts,
  })
  if (data.input !== undefined) {
    data.inputSize = getRoughObjectSize(data.input)
  }

  return {
    // oxlint-disable-next-line no-warning-comments -- intentional reminder, not ready to remove
    data, // TODO: make sure that it is not detached from storage (same reference)
    update(patch) {
      const properPatch = { ...patch }
      if (patch.input) {
        properPatch.input = prepareForLog(patch.input, {
          removeMark: true,
          ...payloadLimits,
          ...inputMaskOpts,
        })
        properPatch.inputSize = getRoughObjectSize(properPatch.input)
      }
      if (patch.output) {
        properPatch.output = prepareForLog(patch.output, {
          removeMark: true,
          ...payloadLimits,
          ...outputMaskOpts,
        })
        properPatch.outputSize = getRoughObjectSize(properPatch.output)
      }
      this.data = {
        ...this.data,
        ...properPatch,
        attributes:
          properPatch.attributes || this.data.attributes
            ? { ...this.data.attributes, ...properPatch.attributes }
            : undefined,
      }
      storage.update(this.data)
    },
    end(patch, opts) {
      if (this.data.duration !== undefined) return // eg we can end span with error in span callback (instead of throwing it) and internally it shouldn't end it again. span with 0 duration are also valid

      // Apply error mapper if configured
      const mappedError = patch.error && mapError(patch.error)

      const patchError = mappedError
        ? prepareTelError({
            error: mappedError,
            span: this.data,
            levelOverride: opts?.errorLevel,
          })
        : undefined
      if (patchError) this.data.error = patchError

      const patchOutput = prepareForLog(patch.output, {
        removeMark: true,
        ...payloadLimits,
        ...outputMaskOpts,
      })

      if (!(patchOutput instanceof Response)) {
        this.data.output = patchOutput
        if (patchOutput !== undefined) {
          this.data.outputSize = getRoughObjectSize(patchOutput)
        }
      }

      const endedAt = Date.now()
      this.data.duration = endedAt - createdAt
      this.data.timestamp = endedAt

      const status =
        this.data.error?.level === 'warning'
          ? 'warning'
          : this.data.error
            ? 'error'
            : 'ok'
      this.data.status = status

      const storagePatch: Partial<SpanData> & { id: string } = {
        error: this.data.error,
        output: this.data.output,
        outputSize: this.data.outputSize,
        duration: this.data.duration,
        timestamp: this.data.timestamp,
        id: this.data.id,
        status,
      }
      if (patch.name) storagePatch.name = patch.name
      if (patch.attributes) {
        this.data.attributes = { ...this.data.attributes, ...patch.attributes }
        storagePatch.attributes = patch.attributes
      }
      if (status === 'error') {
        const { userId, companyId } = this.data
        storage.errors.create(mappedError, safe(this.data.error), {
          ...(userId && { userId }),
          ...(companyId && { companyId }),
        }, opts)
      }
      storage.end(storagePatch)
    },
    _getBaggage() {
      return getBaggage(this.data)
    },
  }
}
